import { useRef } from 'react';

import { ArticleLoginWall } from './ArticleLoginWall';
import { handleClick } from './handleClick';
import styles from './InlineHtml.module.css';
import { useLoginWall } from './useLoginWall';

export interface InlineHtmlProps {
  /** CMS component tags; `TIPS_PREMIUM` gates the login wall. */
  componentTags?: string[];
  /** CMS HTML, already geo-filtered server-side (filterHtmlByCountry) by the caller. */
  html: string;
  /** Whether the visitor is logged in (RR7: `!!myocData.myOCUser`; atlas-astro: getUserContext). */
  isLoggedIn: boolean;
  /** Open the login view (login wall CTA). */
  onLogin: () => void;
  /** Open the signup view (login wall CTA). Wired per app to its auth mechanism. */
  onSignup: () => void;
  /** Base URL for static assets — forwarded to the login wall's background image. */
  resourcesUrl: string;
}

// Single source of truth for the page-template INLINE_HTML, shared by RR7 atlas and atlas-astro. Renders
// either the premium login wall (logged-out TIPS_PREMIUM articles) or the CMS HTML. Auth is the only
// framework seam — delegated to onSignup/onLogin props (see ArticleLoginWall). The CSS-module reset that
// styles the injected HTML lives in InlineHtml.module.css; its descendant selectors (`.inlineHtmlWrapper
// a` …) are global once compiled, so they reach the dangerouslySetInnerHTML children (unlike Astro's
// scoped <style>, which is why the bespoke atlas-astro port previously needed a separate global CSS file).
export const InlineHtml = ({ componentTags, html, isLoggedIn, onLogin, onSignup, resourcesUrl }: InlineHtmlProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { shouldRenderLoginWall } = useLoginWall({ componentTags, containerRef, isLoggedIn });

  return shouldRenderLoginWall ? (
    <ArticleLoginWall onLogin={onLogin} onSignup={onSignup} resourcesUrl={resourcesUrl} />
  ) : (
    <div
      className={styles.inlineHtmlWrapper}
      dangerouslySetInnerHTML={{ __html: html }}
      data-testid="inline-html-div"
      onClick={handleClick}
      ref={containerRef}
    />
  );
};
